/**
 * Backfill `title` on iframes injected by third-party apps or Shopify itself
 * (web pixels, cart progress bars, chat widgets, reviews, etc). These frames
 * are added to the DOM outside our theme code, so we can't author the title
 * in Liquid — we add a sensible default here to satisfy WCAG 4.1.2 / H64.
 *
 * Priority for the fallback title:
 * 1. existing title (left untouched)
 * 2. name attribute — often set by the third party and semantically useful
 * 3. id attribute — e.g. "PBarNextFrame"
 * 4. literal "Embedded content" as a last resort
 */

const describeIframe = (iframe) => {
  if (iframe.hasAttribute('title') && iframe.getAttribute('title').trim() !== '') return;
  const label =
    iframe.getAttribute('name') ||
    iframe.getAttribute('id') ||
    iframe.getAttribute('data-app-name') ||
    'Embedded content';
  iframe.setAttribute('title', label);
};

const scan = (root = document) => {
  root.querySelectorAll('iframe').forEach(describeIframe);
};

// Initial pass after the page has parsed.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan());
} else {
  scan();
}

// Apps often inject iframes after the initial load. Watch the whole document
// and patch anything new.
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.tagName === 'IFRAME') {
        describeIframe(node);
      } else {
        scan(node);
      }
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
